/** Helpers for ML sheet access agent picker — sort by to-location match, search, display. */

export function resolveHubLocationName(locations, locationId) {
  const loc = (locations || []).find(
    (l) => String(l.id ?? l.hub_id) === String(locationId),
  )
  return (loc?.location || loc?.hub_name || loc?.name || '').trim()
}

export function getToLocationNames(toLocationIds, locations) {
  return (toLocationIds || [])
    .map((id) => resolveHubLocationName(locations, id).toLowerCase())
    .filter(Boolean)
}

export function agentMatchesToLocations(agent, toLocationNames, toLocationIds = []) {
  const hubId = String(agent.location_hub_id ?? agent.hub_id ?? '')
  if (hubId && (toLocationIds || []).some((id) => String(id) === hubId)) return true
  if (!toLocationNames?.length) return false
  const agentCity = (agent.city || agent.location || agent.location_name || '').trim().toLowerCase()
  if (!agentCity) return false
  return toLocationNames.some(
    (name) => name === agentCity || agentCity.includes(name) || name.includes(agentCity),
  )
}

export function matchingAccessAgentIds(agents, toLocationIds, locations, excludeUserId) {
  const toNames = getToLocationNames(toLocationIds, locations)
  const skip = String(excludeUserId || '')
  if (!(toLocationIds || []).length) return []
  return (agents || [])
    .filter((agent) => {
      const id = agent.user_id || agent.id
      if (!id || String(id) === skip) return false
      return agentMatchesToLocations(agent, toNames, toLocationIds)
    })
    .map((agent) => agent.user_id || agent.id)
}

export function filterAgentsBySearch(agents, searchTerm) {
  const q = (searchTerm || '').trim().toLowerCase()
  if (!q) return agents || []
  return (agents || []).filter((agent) => {
    const haystack = [
      agent.name,
      agent.city,
      agent.location,
      agent.contact,
      agent.party_name,
      agent.user_id,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })
}

export function groupAccessAgents(agents, searchTerm, toLocationIds, locations) {
  const filtered = filterAgentsBySearch(agents, searchTerm)
  const toNames = getToLocationNames(toLocationIds, locations)
  const matched = []
  const others = []

  for (const agent of filtered) {
    if (toNames.length && agentMatchesToLocations(agent, toNames, toLocationIds)) {
      matched.push(agent)
    } else {
      others.push(agent)
    }
  }

  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  matched.sort(byName)
  others.sort(byName)

  return {
    matched,
    others,
    all: [...matched, ...others],
    hasToFilter: toNames.length > 0,
  }
}

export function formatAccessAgentMeta(agent) {
  const parts = []
  const location = (agent.city || agent.location || agent.location_name || '').trim()
  if (location) parts.push(location)
  if (agent.contact) parts.push(agent.contact)
  if (agent.party_id && agent.party_name) parts.push(agent.party_name)
  return parts.join(' · ')
}

export function getSelectedAccessAgents(agents, selectedIds) {
  const idSet = new Set((selectedIds || []).map(String))
  return (agents || []).filter((agent) => idSet.has(String(agent.user_id || agent.id)))
}
